// What the offline queue knows how to replay.
//
// Every kind of field work that can be captured without a signal needs an entry
// here. The queue used to be a chain of `if (item.type === ...)` branches inside
// the sync loop with no final `else`: a work type nobody had written a branch
// for was marked "syncing", never sent, never removed and never surfaced as an
// error — it just sat in the queueForever. A registry makes the gap impossible
// to miss, because `runSyncHandler` refuses an unknown type out loud.
//
// Each handler receives `{ item, apiFetch }` and returns
// `{ recordType, recordId, message, event }`. Throwing marks the item failed;
// set `error.retryable = false` for something a retry will never fix.
import { accessAttemptFormFields, observationFormFields } from './Components/Address/commentFormFields';
import { sendCodeRecheck } from './Components/CodeRecheck/codeRecheck';

export const isRetryableOfflineResponse = (response) => (
  response?.status === 408 ||
  response?.status === 425 ||
  response?.status === 429 ||
  response?.status >= 500
);

/** Turn a non-OK response into the right kind of error for the queue. */
const failFromResponse = async (response, { noun, conflictNoun = noun }) => {
  const detail = await response.text().catch(() => response.statusText);
  if (response.status === 409) {
    const conflict = new Error(`Conflict needs review before this ${conflictNoun} can sync.`);
    conflict.isConflict = true;
    conflict.conflictReason = detail || 'Server reported a conflict.';
    return conflict;
  }
  if (!isRetryableOfflineResponse(response)) {
    const nonRetryable = new Error(`${noun} was not accepted: ${detail || response.statusText}`);
    nonRetryable.retryable = false;
    return nonRetryable;
  }
  return new Error(`Failed to sync ${noun.toLowerCase()}: ${detail || response.statusText}`);
};

const appendIfPresent = (formData, key, value) => {
  if (value !== null && value !== undefined && value !== '') {
    formData.append(key, String(value));
  }
};

/** Photos captured offline are held in IndexedDB as File/Blob handles. */
const appendFiles = (formData, files, field = 'files') => {
  for (const file of files || []) {
    formData.append(field, file);
  }
};

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

const syncComment = async ({ item, apiFetch }) => {
  const { payload } = item;
  const formData = new FormData();
  formData.append('content', payload.content ?? '');
  formData.append('user_id', payload.user_id);
  formData.append('client_mutation_id', payload.client_mutation_id || item.clientId);
  appendIfPresent(formData, 'mentioned_user_ids', payload.mentioned_user_ids);
  appendIfPresent(formData, 'mentioned_contact_ids', payload.mentioned_contact_ids);
  appendIfPresent(formData, 'unit_id', payload.unit_id);
  appendIfPresent(formData, 'situs_address_id', payload.situs_address_id);
  if (payload.review_later) formData.append('review_later', 'true');

  // Field observations replay through this same path — the observation is a
  // comment, so only the extra form fields differ.
  Object.entries(observationFormFields(payload.observation)).forEach(([key, value]) => {
    appendIfPresent(formData, key, value);
  });
  // A "No access" recorded with no signal replays here. The server dedupes on
  // client_mutation_id, so a replay returns the original comment and never logs
  // the access attempt twice.
  Object.entries(accessAttemptFormFields(payload.access_attempt)).forEach(([key, value]) => {
    appendIfPresent(formData, key, value);
  });

  appendFiles(formData, payload.files);
  if ((payload.existing_blob_ids || []).length > 0) {
    formData.append('existing_blob_ids', JSON.stringify(payload.existing_blob_ids));
  }
  appendIfPresent(formData, 'capture_metadata', payload.capture_metadata);

  const response = await apiFetch(`/comments/${payload.addressId}/address/`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw await failFromResponse(response, { noun: 'Comment' });

  const created = await response.json().catch(() => null);
  return {
    recordType: 'Comment',
    recordId: created?.id,
    message: 'All queued field work uploaded.',
    event: new Event('civiccode:comment-synced'),
  };
};

const syncCodeRecheck = async ({ item }) => {
  const receipt = await sendCodeRecheck(item.payload.violationId, item.payload.recheck, item.payload.files);
  return {
    recordType: 'Violation',
    recordId: item.payload.violationId,
    message: 'Code recheck uploaded.',
    event: new CustomEvent('civicode:recheck-synced', { detail: receipt }),
  };
};

/**
 * Evidence photographed against a violation while offline.
 *
 * `POST /violation/{id}/photos` dedupes on client_mutation_id, so replaying a
 * partial upload re-sends every file and the server keeps one copy of each.
 */
const syncViolationPhotos = async ({ item, apiFetch }) => {
  const { payload } = item;
  const formData = new FormData();
  appendFiles(formData, payload.files);
  formData.append('client_mutation_id', payload.client_mutation_id || item.clientId);
  if ((payload.code_ids || []).length > 0) {
    formData.append('code_ids', JSON.stringify(payload.code_ids));
  }
  appendIfPresent(formData, 'capture_metadata', payload.capture_metadata);

  const response = await apiFetch(`/violation/${payload.violationId}/photos`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw await failFromResponse(response, { noun: 'Photo', conflictNoun: 'photo' });

  return {
    recordType: 'Violation',
    recordId: payload.violationId,
    message: 'Queued violation photos uploaded.',
    event: new CustomEvent('civicode:violation-photos-synced', { detail: { violationId: payload.violationId } }),
  };
};

/**
 * Service of a notice recorded in the field.
 *
 * Posting a notice is exactly the moment an officer is least likely to have a
 * signal — standing at the property — and the correction deadline runs from the
 * date recorded here, so this must survive being captured offline.
 */
const syncViolationService = async ({ item, apiFetch }) => {
  const { payload } = item;
  const formData = new FormData();
  appendFiles(formData, payload.files);
  formData.append('method', payload.method);
  formData.append('served_on', payload.served_on);
  appendIfPresent(formData, 'tracking_number', payload.tracking_number);
  appendIfPresent(formData, 'recipient_name', payload.recipient_name);
  appendIfPresent(formData, 'notes', payload.notes);
  appendIfPresent(formData, 'capture_metadata', payload.capture_metadata);

  const response = await apiFetch(`/violation/${payload.violationId}/serve`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw await failFromResponse(response, { noun: 'Service record', conflictNoun: 'service record' });

  return {
    recordType: 'Violation',
    recordId: payload.violationId,
    message: 'Queued service record uploaded.',
    event: new CustomEvent('civicode:violation-service-synced', { detail: { violationId: payload.violationId } }),
  };
};

/**
 * A correction verified in the field with no signal.
 *
 * Safe to replay now that abatement is a single call: the correction date rides
 * in the payload, so a replay cannot move it forward, and the server short
 * circuits on client_mutation_id rather than abating twice. While it was still
 * a comment upload followed by a separate abate request, queueing it would have
 * meant replaying a sequence that could half-apply.
 */
const syncViolationAbatement = async ({ item, apiFetch }) => {
  const { payload } = item;
  const formData = new FormData();
  appendFiles(formData, payload.files);
  formData.append('code_ids', JSON.stringify(payload.code_ids || []));
  formData.append('verification_method', payload.verification_method);
  formData.append('abated_on', payload.abated_on);
  formData.append('client_mutation_id', payload.client_mutation_id || item.clientId);
  appendIfPresent(formData, 'verification_note', payload.verification_note);
  appendIfPresent(formData, 'capture_metadata', payload.capture_metadata);

  const response = await apiFetch(`/violation/${payload.violationId}/abate-codes`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw await failFromResponse(response, { noun: 'Correction', conflictNoun: 'correction' });

  return {
    recordType: 'Violation',
    recordId: payload.violationId,
    message: 'Queued correction uploaded.',
    event: new CustomEvent('civicode:violation-abated-synced', { detail: { violationId: payload.violationId } }),
  };
};

export const SYNC_HANDLERS = {
  comment: syncComment,
  code_recheck: syncCodeRecheck,
  violation_photos: syncViolationPhotos,
  violation_service: syncViolationService,
  violation_abatement: syncViolationAbatement,
};

export const SUPPORTED_WORK_TYPES = Object.keys(SYNC_HANDLERS);

/**
 * Replay one queued item.
 *
 * An unregistered type is a programming error, and the one thing it must not do
 * is fail quietly: the officer's work would stay in the queue looking like it
 * was on its way up. Refuse it as non-retryable so it lands in the failed state
 * the queue UI already surfaces.
 */
export const runSyncHandler = async ({ item, apiFetch }) => {
  const handler = SYNC_HANDLERS[item?.type];
  if (!handler) {
    const unsupported = new Error(
      `This app cannot upload "${item?.type}" work. Update the app, then retry — ` +
      'your saved work is still on this device.'
    );
    unsupported.retryable = false;
    unsupported.unsupportedType = true;
    throw unsupported;
  }
  return handler({ item, apiFetch });
};
